/** Sayfanın üstünde yüzen üçüncü taraf widget'larını geçici olarak gizler.
 *
 * Hazır spor kitabı (Betby / Flexrix) tam ekran çalışır ve sağlayıcının kupon
 * çubuğu ekranın sağ alt köşesindedir. Aynı köşede duran canlı destek düğmesi
 * kuponu ve "Bahis yap" düğmesini örtüyordu.
 *
 * Sitenin kendi LiveChat widget'ı `liveChatConfig.suppressLiveChat` ile
 * susturulur; **bu modül onun yetmediği durum içindir**: canlı destek çoğu
 * kurulumda panelden girilen özel koddan gelir
 * ([customCode.js](./customCode.js) — Ayarlar → Özel Kod). O script kendi
 * düğümlerini çalışma anında `<body>`ye ekler; ne React ağacımızdadır ne de
 * `window.LiveChatWidget` API'sini taşır, dolayısıyla kod tarafından
 * "kapat" denemez. Tek tutamağımız DOM'un kendisidir.
 *
 * İki kapı birlikte çalışır:
 *
 * * **Tanınan sağlayıcılar** — yaygın sohbet widget'larının kapsayıcıları
 *   adıyla bilinir; ölçüye bakılmadan gizlenir.
 * * **Ölçüye dayalı sezgi** — `<body>`nin doğrudan çocukları arasında React
 *   kökünün *dışında* kalan, `position: fixed` ile ekranın alt yarısına
 *   demirlenmiş katmanlar. Uygulamanın kendi yüzen parçaları (alt menü, yukarı
 *   çık düğmesi, modallar) React kökünün içindedir ve bu taramaya hiç girmez.
 *
 * Gizleme **inline stille değil işaretle** yapılır: widget'ların çoğu kendi
 * `style` özniteliğini periyodik olarak yeniden yazar ve inline `display`
 * sessizce geri gelirdi. İşaret kaldırıldığında widget aynen geri döner —
 * script'e dokunulmaz, ikinci kez çalıştırılmaz (bkz. customCode.js).
 */

const HIDDEN_ATTR = 'data-bhs-widget-hidden';
const STYLE_ID = 'bhs-widget-hidden-style';
const ROOT_ID = 'root';

// Panelden girilen koddan gelen yaygın sohbet widget'ları. Liste kapsayıcı
// değildir, olamaz da — asıl kapı aşağıdaki ölçü sezgisidir; bu liste yalnızca
// React kökünün içine ya da derinlere enjekte eden sağlayıcıları da yakalar.
const KNOWN_SELECTORS = [
  '#chat-widget-container', // LiveChat
  '#tawkchat-container', '.tawk-min-container', // Tawk.to
  'jdiv', // JivoChat
  '#crisp-chatbox', // Crisp
  '#tidio-chat', // Tidio
  '#fc_frame', // Freshchat
  '.zsiq_floatmain', // Zoho SalesIQ
  '#smartsupp-widget-container', // Smartsupp
  '#chaport-container', // Chaport
  '#hubspot-messages-iframe-container', // HubSpot
  '#intercom-container', '.intercom-lightweight-app', // Intercom
].join(',');

// Sonradan yüklenen widget'lar kapsayıcılarını önce boş ve stilsiz ekler,
// biçimi saniyeler sonra alır. Düğüm eklendiğinde tarama yapılır; bu gecikmeli
// turlar "eklendi ama henüz yüzmüyordu" hâlini yakalar.
const RESCAN_DELAYS_MS = [400, 1200, 3000, 8000];

const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT', 'TEMPLATE', 'META', 'TITLE', 'BASE',
]);

const ensureStyle = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `[${HIDDEN_ATTR}]{display:none !important;}`;
  document.head?.appendChild(style);
};

/** Eleman ekranın alt yarısına demirlenmiş yüzen bir katman mı? */
const isFloatingOverlay = (element) => {
  const view = element.ownerDocument?.defaultView;
  if (!view) return false;

  let computed;
  try {
    computed = view.getComputedStyle(element);
  } catch {
    return false;
  }
  if (!computed || computed.position !== 'fixed') return false;
  if (computed.display === 'none' || computed.visibility === 'hidden') return false;

  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;

  const height = view.innerHeight || 0;
  const width = view.innerWidth || 0;
  if (!height || !width) return false;

  // Tam ekran kaplayan katman bir düğme değil, bir sayfadır (ör. sitenin kendi
  // kilit ekranı): ona dokunulmaz. Ölçü **yalnızca yükseklik** üzerindedir —
  // genişlik de arandığında telefonda hiçbir widget yakalanmıyordu: dar
  // ekranda sohbet kapsayıcıları kenardan kenara uzanır (`width: 100%`) ve
  // kısa bir launcher bile "tam ekran sayfa" sanılıp atlanıyordu.
  if (rect.height >= height * 0.9) return false;

  return rect.bottom > height * 0.5;
};

/** Kapsayıcının kendisi ya da içindeki küçük bir düğüm yüzüyor mu?
 *
 * Widget'ların bir kısmı `<body>`ye stilsiz bir sarmalayıcı bırakır ve asıl
 * düğmeyi onun içine koyar; yalnızca doğrudan çocuğa bakan bir kontrol o
 * sarmalayıcıyı görmezdi. Tarama derinliği bilinçli olarak sınırlıdır —
 * amaç birkaç düğümlük widget kabuğu, sayfanın tamamı değil. */
const SUBTREE_SCAN_LIMIT = 60;

const containsFloatingOverlay = (element) => {
  if (isFloatingOverlay(element)) return true;
  const descendants = element.querySelectorAll('*');
  if (descendants.length > SUBTREE_SCAN_LIMIT) return false;
  for (let i = 0; i < descendants.length; i += 1) {
    if (isFloatingOverlay(descendants[i])) return true;
  }
  return false;
};

/** Yüzen üçüncü taraf widget'larını gizler; dönen fonksiyon geri getirir. */
export const suppressFloatingWidgets = () => {
  if (typeof document === 'undefined') return () => {};
  ensureStyle();

  const hidden = new Set();
  const timers = [];

  const hide = (element) => {
    if (!element || hidden.has(element)) return;
    hidden.add(element);
    element.setAttribute(HIDDEN_ATTR, '');
  };

  const scan = () => {
    document.querySelectorAll(KNOWN_SELECTORS).forEach(hide);

    const root = document.getElementById(ROOT_ID);
    Array.from(document.body?.children || []).forEach((element) => {
      if (SKIP_TAGS.has(element.tagName)) return;
      // Uygulamanın kendi yüzen parçaları (alt menü, modallar) React kökünün
      // içindedir; kökü gizlemek siteyi karartırdı.
      if (root && (element === root || element.contains(root))) return;
      if (element.hasAttribute(HIDDEN_ATTR)) return;
      if (containsFloatingOverlay(element)) hide(element);
    });
  };

  scan();
  RESCAN_DELAYS_MS.forEach((delay) => {
    timers.push(setTimeout(scan, delay));
  });

  const observer = typeof MutationObserver === 'function'
    ? new MutationObserver(scan)
    : null;
  if (observer && document.body) {
    observer.observe(document.body, { childList: true });
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    timers.forEach(clearTimeout);
    observer?.disconnect();
    hidden.forEach((element) => element.removeAttribute(HIDDEN_ATTR));
    hidden.clear();
  };
};

/* ======================================================== yerleştirme

 * Yukarıdaki işlev widget'ı **gizler**; bu bölüm onu **yerine koyar**.
 *
 * Panelden girilen destek kodunun bir kısmı kendi düğmesini konumlandırmaz:
 * `<body>`ye akışta duran bir bant bırakır ve bant sayfanın en üstüne,
 * kabuğun da üstüne düşer (canlıda ölçüldü: "CANLI DESTEK İLE İLETİŞİME
 * GEÇİN" başlığın üzerinde tam genişlikte bir şerit olarak çıkıyordu).
 * Bant orada hem tasarımı bozar hem de oyuncunun aradığı yerde — sağ alt
 * köşede — bir düğme bırakmaz.
 *
 * İki hâl ayrılır ve ayrım **bilinçlidir**:
 *
 * * **Kendi kendini konumlandıran** widget (zaten `position: fixed`) yerinde
 *   bırakılır; yalnızca alt barın üstüne çıkarılır. Böyle bir widget'ı
 *   köşeye çakmak, açıldığında sohbet penceresini kırpardı.
 * * **Akışta duran** düğüm alt barın üstüne, kenardan kenara uzanan ince
 *   bir şeride çevrilir — asıl arıza odur. Köşeye alınan dar bir balon
 *   denendi ve bırakıldı: panelden gelen bant kendi genişliğini metninden
 *   alıyor, köşede oyun ızgarasının üstüne biniyordu. Şerit ise akışın
 *   dışında ama kimsenin üstünde değil; içerik alt boşluğu onun kadar
 *   büyür (aşağıdaki `DOCKED_FLAG`).
 *
 * Yerleştirme **yalnızca destek görünen** düğümlere uygulanır: panelden gelen
 * kod sayaç, piksel ya da duyuru bandı da olabilir ve tanınmayan bir bandı
 * köşeye çakmak yöneticinin bilerek koyduğu duyuruyu bozardı.
 */

const DOCK_ATTR = 'data-bhs-widget-dock';
const LIFT_ATTR = 'data-bhs-widget-lift';
const DOCK_STYLE_ID = 'bhs-widget-dock-style';

// Şerit yerleştirildiğinde `<html>` işaretlenir. Şema içerik alt boşluğunu
// **yalnızca** bu işaret varken büyütür: koşulsuz büyütülseydi destek kodu
// tanımlı olmayan kurulumlarda sayfanın altında sebepsiz bir boşluk kalırdı.
const DOCKED_FLAG = 'data-bhs-docked';

// Destek düğümünün imzası: kimlik, sınıf, etiket ya da metin. Metin de
// aranır çünkü panelden gelen bandın çoğu zaman sınıfı bile yoktur — tek
// ayırt edici işareti üstündeki yazıdır.
const SUPPORT_HINT = /(canl[ıi]\s*destek|destek|sohbet|live\s*chat|livechat|\bchat\b|support|whatsapp|telegram)/i;

// Metnin tamamı taranmaz: panelden gelen kod bütün bir sayfa da basabilir ve
// uzun metinde "chat" geçen sıradan bir içerik destek sanılırdı.
const SUPPORT_TEXT_LIMIT = 200;

const ensureDockStyle = () => {
  if (document.getElementById(DOCK_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = DOCK_STYLE_ID;
  // Kurallar `!important` taşır: widget'ların çoğu kendi `style` özniteliğini
  // periyodik olarak yeniden yazar ve sade bir kural sessizce ezilirdi
  // (gizlemedeki gerekçenin aynısı).
  // Şerit alt barın **hemen üstüne** oturur (aralarında boşluk yok) ve
  // kenardan kenara uzanır: oyuncunun tek işi üstüne basmaktır. Katman
  // numarası alt barınkinin altındadır — ölçüler kayarsa gezinme kazanmalı,
  // destek şeridi menüyü örtmemelidir.
  //
  // **`display` bilinçli olarak yazılmaz.** Panelden gelen kodun bir kısmı
  // düğmesini `display: none` ile gizli tutup yalnızca kendi seçtiği sayfada
  // açıyor; buraya `display: flex !important` konduğunda o gizleme eziliyor
  // ve düğme gizlenmesi gereken her sayfada ekrana yapışıyordu (canlıda
  // ölçüldü). Görünürlük sayfanın kendi kararıdır; yerleştirme yalnızca
  // **zaten görünen** bir düğümün yerini söyler.
  style.textContent = `
[${DOCK_ATTR}]{position:fixed !important;left:0 !important;right:0 !important;top:auto !important;bottom:var(--bhs-dock-bottom, 0px) !important;width:auto !important;max-width:none !important;margin:0 !important;border-radius:0 !important;min-height:var(--bhs-dock-height, 40px) !important;align-items:center !important;justify-content:center !important;text-align:center !important;cursor:pointer !important;z-index:54 !important;}
[${LIFT_ATTR}]{bottom:var(--bhs-widget-bottom, 22px) !important;}
`;
  document.head?.appendChild(style);
};

/** Eleman ekranda yer kaplıyor mu? (0x0 piksel ve gizli düğümler elenir) */
const isVisibleNode = (element) => {
  const view = element.ownerDocument?.defaultView;
  if (!view) return false;
  let computed;
  try {
    computed = view.getComputedStyle(element);
  } catch {
    return false;
  }
  if (!computed || computed.display === 'none' || computed.visibility === 'hidden') {
    return false;
  }
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/** Düğüm bir destek widget'ına benziyor mu? */
const looksLikeSupport = (element) => {
  try {
    if (element.matches?.(KNOWN_SELECTORS)) return true;
  } catch {
    // Seçici listesi tarayıcıda desteklenmeyen bir şey içeriyorsa eşleşme
    // denemesi atılır; metin taraması yine çalışır.
  }
  const marks = [
    element.id,
    typeof element.className === 'string' ? element.className : '',
    element.getAttribute?.('aria-label') || '',
    element.getAttribute?.('title') || '',
  ].join(' ');
  if (SUPPORT_HINT.test(marks)) return true;
  return SUPPORT_HINT.test((element.textContent || '').slice(0, SUPPORT_TEXT_LIMIT));
};

/** Panelden gelen destek düğmesini alt barın üstüne, sağ köşeye yerleştirir. */
export const dockSupportWidgets = () => {
  if (typeof document === 'undefined') return () => {};
  ensureDockStyle();

  const marked = new Map();
  const timers = [];

  /* Panelden gelen kodun bir kısmı düğümü **sonradan yerine taşır**: destek
   * düğmesini bulup footer'ın hemen önüne sokar, yani düğüm `<body>`nin
   * çocuğu olmaktan çıkıp uygulamanın ağacına girer. İşaret orada kalırsa
   * `position: fixed` kuralı düğmeyi taşındığı yerde de ekrana çakar ve
   * kod kendi işini doğru yaptığı hâlde düğme yine yüzer görünür.
   * Bu yüzden her turda `<body>`den ayrılmış düğümlerin işareti bırakılır. */
  const prune = () => {
    marked.forEach((attr, element) => {
      if (element.isConnected && element.parentNode === document.body) return;
      element.removeAttribute(attr);
      marked.delete(element);
    });
  };

  // `<html>` işareti elde kalan şeride göre yazılır: son şerit de
  // taşındığında içerik alt boşluğu geri kapanmalıdır.
  const syncFlag = () => {
    let docked = false;
    marked.forEach((attr) => {
      if (attr === DOCK_ATTR) docked = true;
    });
    const html = document.documentElement;
    if (!html) return;
    if (docked) html.setAttribute(DOCKED_FLAG, '');
    else html.removeAttribute(DOCKED_FLAG);
  };

  const scan = () => {
    prune();
    const root = document.getElementById(ROOT_ID);
    Array.from(document.body?.children || []).forEach((element) => {
      if (SKIP_TAGS.has(element.tagName)) return;
      // Uygulamanın kendi ağacı React kökünün içindedir; launcher'ımızın
      // yeri şemanın CSS'inden gelir, buradan değil.
      if (root && (element === root || element.contains(root))) return;
      if (marked.has(element)) return;
      if (!isVisibleNode(element)) return;
      if (!looksLikeSupport(element)) return;
      const attr = containsFloatingOverlay(element) ? LIFT_ATTR : DOCK_ATTR;
      marked.set(element, attr);
      element.setAttribute(attr, '');
    });
    syncFlag();
  };

  scan();
  // Widget'lar kapsayıcılarını önce boş ekler, biçimi saniyeler sonra alır;
  // gecikmeli turlar "eklendi ama henüz ölçülemiyordu" hâlini yakalar.
  RESCAN_DELAYS_MS.forEach((delay) => {
    timers.push(setTimeout(scan, delay));
  });

  const observer = typeof MutationObserver === 'function'
    ? new MutationObserver(scan)
    : null;
  if (observer && document.body) {
    observer.observe(document.body, { childList: true });
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    timers.forEach(clearTimeout);
    observer?.disconnect();
    marked.forEach((attr, element) => element.removeAttribute(attr));
    marked.clear();
    document.documentElement?.removeAttribute(DOCKED_FLAG);
  };
};

export default suppressFloatingWidgets;
